import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Search, Download, RefreshCw, Table as TableIcon, Calendar as CalendarIcon,
    MoreVertical, Columns, LayoutList, ChevronDown, User, Phone, Mail,
    MapPin, Building2, FileText, IndianRupee, ShoppingCart
} from 'lucide-react';
import { Icon } from '@iconify/react';
import PremiumModuleWrapper from '../Task/Components/common/PremiumModuleWrapper';
import PremiumModuleCards from '../Task/Components/common/PremiumModuleCards';
import SubHeader from '../../components/common/SubHeader/SubHeader';
import DynamicCalendar from '../../components/DynamicCalendar/DynamicCalendar';
import GridMainPanelLayout from '../../components/common/Panels/GridMainPanelLayout';
import Loader from '../../components/Loader/Loader';
import { getSession } from '../../getSession';
import API_ENDPOINTS from '../../config/apiEndpoints';
import axios from 'axios';
import { conversionTypes } from './constants';
import { getInitials } from '../../utils/helpers';
import { formatDate } from '../Task/Components/common/premiumUtils';
import './Conversions.css';
import { Box, Typography } from '@mui/material';
import usePermissions from '../Master_Settings/usePermissions';
import useFieldMasking from '../Master_Settings/useFieldMasking';
import { getConversionDetail } from '../../services/conversionService';

const ConversionDetailSubComponent = ({ leadNo }) => {
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchDetails = async () => {
            try {
                setLoading(true);
                const { TokenId, userId } = getSession();
                const payload = {
                    Token: String(TokenId || '-2295521862261168'),
                    LoggedUserId: String(userId || '34594'),
                    Details: {
                        ParentId: String(userId || '34594'),
                        LeadNo: String(leadNo)
                    }
                };
                const response = await getConversionDetail(payload);
                if (isMounted) {
                    if (response.Status === 1 && response.Details) {
                        setDetails(response.Details);
                    } else {
                        setError('No details found');
                    }
                }
            } catch (err) {
                if (isMounted) setError('Failed to load details');
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchDetails();
        return () => { isMounted = false; };
    }, [leadNo]);

    if (loading) {
        return <div className="p-4 flex justify-center"><Loader message="Loading details..." /></div>;
    }

    if (error || details.length === 0) {
        return <div className="p-4 text-center text-slate-500">No details available.</div>;
    }

    return (
        <div className="bg-slate-50 p-4 border-t border-slate-200 shadow-inner">
            <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs uppercase bg-slate-200 text-slate-700">
                    <tr>
                        <th className="px-4 py-3 rounded-tl-lg">Followup Status</th>
                        <th className="px-4 py-3">Ordered Date</th>
                        <th className="px-4 py-3">Products</th>
                        <th className="px-4 py-3">Amount Paid</th>
                        <th className="px-4 py-3">Assign To</th>
                        <th className="px-4 py-3 rounded-tr-lg">Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    {details.map((item, idx) => (
                        <tr key={idx} className="bg-white border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                            <td className="px-4 py-3 font-medium text-slate-800">{item.FollowupStatus || '-'}</td>
                            <td className="px-4 py-3">{item.OrderDate ? formatDate(item.OrderDate) : '-'}</td>
                            <td className="px-4 py-3">{item.Products || '-'}</td>
                            <td className="px-4 py-3 font-bold text-slate-900">₹{item.AmountPaid || 0}</td>
                            <td className="px-4 py-3">{item.AssignedTo || '-'}</td>
                            <td className="px-4 py-3 max-w-xs truncate" title={item.Remarks}>{item.Remarks || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const Conversions = () => {
    const { permissions } = usePermissions('PAG10033');
    const { maskObject, isHiddenField } = useFieldMasking('PAG10033');
    const crmOperationPerms = useMemo(() => {
        if (!permissions || permissions.length === 0) return null;
        return permissions[0];
    }, [permissions]);

    const [conversions, setConversions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [totalRecord, setTotalRecord] = useState(0);
    const [todayGenerated, setTodayGenerated] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [mode, setMode] = useState(conversionTypes.CONSOLIDATED);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [viewMode, setViewMode] = useState('table');
    const [rowSelection, setRowSelection] = useState({});
    const [selectedConversion, setSelectedConversion] = useState(null);

    // Column Visibility State
    const [visibleColumns, setVisibleColumns] = useState({
        LeadNo: true,
        PersonName: true,
        CompanyName: true,
        MobileNo1: true,
        EmailId: true,
        TotalBusiness: true,
        TotalOrders: true,
        LastOrderDate: true,
        City: true,
        State: true,
        Country: true,
        PinCode: true,
        ResidentialAddress: false,
        OfficeAddress: false,
        LastConvertedBy: true
    });

    const [showColumnSearch, setShowColumnSearch] = useState(false);
    const columnSearchRef = useRef(null);

    const fetchConversions = useCallback(async () => {
        setIsLoading(true);
        try {
            const { TokenId, userId } = getSession();

            // Build the URL-encoded search parameters expected by GetConversionList
            const searchParams = new URLSearchParams();
            searchParams.append('draw', String(currentPage));

            // Add columns structure up to 15 (16 columns total)
            for (let i = 0; i <= 15; i++) {
                searchParams.append(`columns[${i}][data]`, '');
                searchParams.append(`columns[${i}][name]`, '');
                searchParams.append(`columns[${i}][searchable]`, 'true');
                searchParams.append(`columns[${i}][orderable]`, i === 0 ? 'true' : 'false');
                searchParams.append(`columns[${i}][search][value]`, '');
                searchParams.append(`columns[${i}][search][regex]`, 'false');
            }

            searchParams.append('order[0][column]', '0');
            searchParams.append('order[0][dir]', 'asc');
            searchParams.append('start', String((currentPage - 1) * itemsPerPage));
            searchParams.append('length', String(itemsPerPage));
            searchParams.append('search[value]', '');
            searchParams.append('search[regex]', 'false');

            searchParams.append('Token', String(TokenId || '-2295521862261168'));
            searchParams.append('Mode', String(mode));
            searchParams.append('UserId', String(userId || '32222'));
            searchParams.append('UserIds', '');
            searchParams.append('SearchText', '');
            searchParams.append('Contain', String(searchText));
            searchParams.append('FromDate', '');
            searchParams.append('ToDate', '');

            const response = await fetch('https://serviceskit19.azurewebsites.net/UserCRM/UserConversionsList', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: searchParams.toString()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const data = result.data || result.Details || [];
            const total = result.recordsTotal || (data.length > 0 ? (data[0].rcnt || data[0].rc1 || 0) : 0);

            const normalizedData = data.map(item => maskObject({
                ...item,
                MobileNo1: item.MobileNo || item.MobileNo1 || '-',
                EmailId: item.EmailID || item.EmailId || '-',
                TotalBusiness: item.TotalAmount !== undefined ? item.TotalAmount : (item.TotalBusiness || 0),
                LastOrderDate: item.OrderDate ? formatDate(item.OrderDate) : (item.LastOrderDate || '-'),
                LastConvertedBy: item.CreatedBy || item.LastConvertedBy || '-'
            }));

            setConversions(normalizedData);
            setTotalRecord(total);
            setIsInitialLoad(false);
        } catch (error) {
            console.error('Failed to fetch conversions:', error);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, mode, searchText]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchConversions();
        }, 500);
        return () => clearTimeout(timer);
    }, [fetchConversions]);

    const fetchGlobalStats = useCallback(async () => {
        try {
            const { TokenId, userId } = getSession();
            const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
            const Get_Convsersion_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_CONVERSION_DETAILS}`;

            const payload = {
                Status: "",
                Message: "",
                Token: String(TokenId || '-2295521862261168'),
                Details: {
                    UserId: String(userId || '34594'),
                    strConversionSection: "conversions"
                }
            };
            const response = await axios.post(Get_Convsersion_Details, payload);
            if (response.data && response.data.Details) {
                const matrix = response.data.Details.ConversionMatrix_CLS_List;
                if (matrix && matrix.length > 0) {
                    setTodayGenerated(matrix[0].ConvertedToday || 0);
                }
            }
        } catch (error) {
            console.error('Error fetching global conversion stats:', error);
        }
    }, []);

    useEffect(() => {
        fetchGlobalStats();
    }, [fetchGlobalStats]);

    // Close column search on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnSearchRef.current && !columnSearchRef.current.contains(event.target)) {
                setShowColumnSearch(false);
            }
        };
        if (showColumnSearch) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showColumnSearch]);

    const handleSearch = (val) => {
        setSearchText(val);
        setCurrentPage(1);
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setCurrentPage(1);
    };

    const handleRefresh = () => {
        setSearchText('');
        setMode(conversionTypes.CONSOLIDATED);
        setCurrentPage(1);
        fetchConversions();
    };

    const handleDownload = () => {
        try {
            const { userId, TokenId } = getSession();
            const exportUrl = 'https://serviceskit19.azurewebsites.net/Export/UserConversionReport';

            const existingForm = document.getElementById('frmExportData');
            if (existingForm) {
                existingForm.remove();
            }

            const form = document.createElement('form');
            form.id = 'frmExportData';
            form.action = exportUrl;
            form.method = 'POST';
            form.target = '_blank';
            form.style.display = 'none';

            const addInput = (name, value) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                input.value = value;
                form.appendChild(input);
            };

            addInput('Token', TokenId || '-2295521862261168');
            addInput('Mode', mode);
            addInput('UserId', userId || '34594');
            addInput('UserIds', '');
            addInput('SearchText', searchText);
            addInput('Contain', searchText);
            addInput('FromDate', '');
            addInput('ToDate', '');

            document.body.appendChild(form);
            form.submit();

            setTimeout(() => {
                const frm = document.getElementById('frmExportData');
                if (frm) frm.remove();
            }, 1000);

        } catch (error) {
            console.error('Failed to export conversions:', error);
        }
    };

    const columns = useMemo(() => {
        const allCols = [
            ...(mode === conversionTypes.CONSOLIDATED ? [{
                id: 'expand',
                header: '',
                accessorKey: 'expand',
                cell: ({ row }) => {
                    return row.getCanExpand() ? (
                        <button
                            onClick={row.getToggleExpandedHandler()}
                            style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', padding: 0 }}
                        >
                            <Icon icon={row.getIsExpanded() ? 'ri:subtract-line' : 'ri:add-line'} />
                        </button>
                    ) : null;
                },
            }] : []),
            {
                id: 'LeadNo',
                header: 'Lead No',
                accessorKey: 'LeadNo',
                cell: info => (
                    <span className="font-semibold text-slate-800">
                        {info.getValue()}
                    </span>
                )
            },
            {
                id: 'PersonName',
                header: 'Name',
                accessorKey: 'PersonName',
                cell: info => (
                    <span className="font-semibold text-slate-800">
                        {info.getValue()}
                    </span>
                )
            },
            {
                id: 'CompanyName',
                header: 'Company Name',
                accessorKey: 'CompanyName',
            },
            {
                id: 'MobileNo1',
                header: 'Mobile',
                accessorKey: 'MobileNo1',
            },
            {
                id: 'EmailId',
                header: 'Email',
                accessorKey: 'EmailId',
            },
            {
                id: 'TotalBusiness',
                header: 'Total Business',
                accessorKey: 'TotalBusiness',
                cell: info => <span className="font-bold text-slate-900">₹{info.getValue() || 0}</span>
            },
            {
                id: 'TotalOrders',
                header: 'Total Orders',
                accessorKey: 'TotalOrders',
            },
            {
                id: 'LastOrderDate',
                header: 'Last Order Date',
                accessorKey: 'LastOrderDate',
            },
            {
                id: 'City',
                header: 'City',
                accessorKey: 'City',
            },
            {
                id: 'State',
                header: 'State',
                accessorKey: 'State',
            },
            {
                id: 'Country',
                header: 'Country',
                accessorKey: 'Country',
            },
            {
                id: 'PinCode',
                header: 'Pin Code',
                accessorKey: 'PinCode',
            },
            {
                id: 'ResidentialAddress',
                header: 'Residential Address',
                accessorKey: 'ResidentialAddress',
            },
            {
                id: 'OfficeAddress',
                header: 'Office Address',
                accessorKey: 'OfficeAddress',
            },
            {
                id: 'LastConvertedBy',
                header: 'Last Converted By',
                accessorKey: 'LastConvertedBy',
            }
        ];

        return allCols.filter(col => col.id === 'expand' || (visibleColumns[col.id] && !isHiddenField(col.accessorKey || col.id)));
    }, [visibleColumns, isHiddenField, mode]);

    const handleColumnToggle = (colId) => {
        setVisibleColumns(prev => ({
            ...prev,
            [colId]: !prev[colId]
        }));
    };

    const meatballMenu = (
        <div className="relative" ref={columnSearchRef}>
            <button
                onClick={() => setShowColumnSearch(!showColumnSearch)}
                className="menu-action-btn"
                title="List Actions"
            >
                <MoreVertical size={20} />
            </button>

            {showColumnSearch && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-2xl z-[1000] overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">List Actions</span>
                    </div>

                    {/* Import/Export */}
                    {crmOperationPerms?.Export !== false && (
                        <button
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition text-sm text-slate-700 border-b border-slate-100"
                            onClick={() => { handleDownload(); setShowColumnSearch(false); }}
                        >
                            <Download size={16} className="text-slate-400" />
                            <span>Export Conversions</span>
                        </button>
                    )}

                    {/* Column Search (Visibility) */}
                    <div className="px-4 py-2 bg-slate-50/50">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Column Search</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                        {Object.keys(visibleColumns).map(colId => (
                            <label key={colId} className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 cursor-pointer transition">
                                <input
                                    type="checkbox"
                                    checked={visibleColumns[colId]}
                                    onChange={() => handleColumnToggle(colId)}
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-600 capitalize">
                                    {colId.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="conversions-page-container flex flex-col h-full bg-slate-50/50 overflow-hidden">
            <SubHeader
                pageName="Conversions"
                pageCode="PAG10033"
                title="Conversions"
                searchQuery={searchText}
                setSearchQuery={handleSearch}
                searchPlaceholder="Search By: Lead No, Name, Mobile, Email..."
                viewMode={viewMode}
                setViewMode={setViewMode}
                showAdd={false}
                showSearch={!!crmOperationPerms?.Search}
                showRefresh={true}
                onRefreshClick={handleRefresh}
                showSettings={true}
                showAdvanceFilter={false}
                showMoreVertical={true}
                showViewSwitcher={true}
                showGrid={true}
                showTable={true}
                showCalendar={false}
                totalCount={totalRecord}
                customMenuItems={meatballMenu}
            >
                {/* Mode Selectors in Header */}
                <div className="flex items-center gap-6 ml-4 border-l pl-6 border-slate-200">
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                            type="radio"
                            name="conversionMode"
                            checked={mode === conversionTypes.CONSOLIDATED}
                            onChange={() => handleModeChange(conversionTypes.CONSOLIDATED)}
                            className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                            Consolidated
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                            type="radio"
                            name="conversionMode"
                            checked={mode === conversionTypes.INDIVIDUAL}
                            onChange={() => handleModeChange(conversionTypes.INDIVIDUAL)}
                            className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                            Individual
                        </span>
                    </label>
                </div>
            </SubHeader>

            {(viewMode === 'table' || viewMode === 'grid') && (
                <PremiumModuleCards
                    stats={{ total: totalRecord }}
                    moduleName="Conversion"
                    onStatusClick={(key) => key === 'ResetAll' && handleRefresh()}
                    customCards={[
                        {
                            title: totalRecord,
                            subtitle: 'Total Conversion',
                            icon: 'ri:exchange-funds-line',
                            color: 'primary',
                            key: 'All'
                        },
                        {
                            title: todayGenerated,
                            subtitle: 'Today Generated',
                            icon: 'ri:calendar-check-line',
                            color: 'success',
                            key: 'Today'
                        }
                    ]}
                    showToggle={true}
                    px="24px"
                />
            )}

            <div className="flex-1 overflow-hidden p-6 pt-0">
                {!isLoading && !isInitialLoad && conversions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full w-full mt-10">
                        <img src="/nodata.gif" alt="No records found" style={{ width: '350px', opacity: 0.9, objectFit: 'contain' }} />
                        <h3 className="text-lg font-bold text-slate-600 mt-4">No records found</h3>
                    </div>
                ) : viewMode === 'grid' ? (
                    <GridMainPanelLayout
                        type="conversion"
                        data={conversions.map(c => ({
                            ...c,
                            id: c.LeadNo || c.id,
                            title: c.PersonName || 'No Name',
                            name: c.PersonName || 'No Name',
                            date: c.LastOrderDate || '-',
                            MobileNo1: c.MobileNo || c.MobileNo1 || '-',
                            EmailId: c.EmailID || c.EmailId || '-',
                            TotalBusiness: c.TotalAmount !== undefined ? c.TotalAmount : (c.TotalBusiness || 0),
                            _badgeText: c.LastConvertedBy || 'Converted',
                            _statusClass: 'completed'
                        }))}
                        loading={isLoading}
                        totalRecords={totalRecord}
                        currentPage={currentPage}
                        totalPages={Math.ceil(totalRecord / itemsPerPage)}
                        onPageChange={setCurrentPage}
                        selectedItem={selectedConversion}
                        onSelectItem={setSelectedConversion}
                        getInitials={getInitials}
                        renderDetailContent={(item) => (
                            <div className="p-8 space-y-8 bg-white h-full overflow-y-auto">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-5">
                                        <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100 shadow-sm">
                                            <User size={32} />
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-slate-900 leading-tight">{item.PersonName}</h2>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wider border border-blue-100">
                                                    Lead #{item.LeadNo}
                                                </span>
                                                <span className="text-slate-400 text-sm">•</span>
                                                <span className="text-slate-500 text-sm font-medium">{item.City}, {item.Country}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-100 flex flex-col items-end">
                                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Status</span>
                                        <span className="text-emerald-700 font-bold text-sm">Converted</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100 hover:border-blue-200 transition-all group">
                                        <div className="flex items-center gap-3 mb-1">
                                            <div className="p-2 rounded-lg bg-white shadow-sm text-blue-600 group-hover:scale-110 transition-transform">
                                                <IndianRupee size={18} />
                                            </div>
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Business</span>
                                        </div>
                                        <div className="text-xl font-black text-slate-900 ml-10">₹{item.TotalBusiness || 0}</div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100 hover:border-amber-200 transition-all group">
                                        <div className="flex items-center gap-3 mb-1">
                                            <div className="p-2 rounded-lg bg-white shadow-sm text-amber-600 group-hover:scale-110 transition-transform">
                                                <ShoppingCart size={18} />
                                            </div>
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Orders</span>
                                        </div>
                                        <div className="text-xl font-black text-slate-900 ml-10">{item.TotalOrders || 0}</div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-[0.15em] flex items-center gap-2">
                                        Contact Information
                                        <div className="h-[1px] flex-1 bg-slate-100"></div>
                                    </h3>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                                                <div className="p-2.5 rounded-lg bg-white shadow-sm text-slate-400 border border-slate-100">
                                                    <Building2 size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Company</div>
                                                    <div className="text-sm font-semibold text-slate-700">{item.CompanyName || '-'}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                                                <div className="p-2.5 rounded-lg bg-white shadow-sm text-slate-400 border border-slate-100">
                                                    <Phone size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Mobile</div>
                                                    <div className="text-sm font-semibold text-slate-700">{item.MobileNo1 || '-'}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                                                <div className="p-2.5 rounded-lg bg-white shadow-sm text-slate-400 border border-slate-100">
                                                    <Mail size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Email</div>
                                                    <div className="text-sm font-semibold text-slate-700 truncate max-w-[180px]">{item.EmailId || '-'}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                                                <div className="p-2.5 rounded-lg bg-white shadow-sm text-slate-400 border border-slate-100">
                                                    <CalendarIcon size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Last Order</div>
                                                    <div className="text-sm font-semibold text-slate-700">{item.LastOrderDate || '-'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-[0.15em] flex items-center gap-2">
                                        Location Details
                                        <div className="h-[1px] flex-1 bg-slate-100"></div>
                                    </h3>
                                    <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100 flex items-start gap-4">
                                        <div className="p-3 rounded-xl bg-white shadow-sm text-slate-400 border border-slate-100">
                                            <MapPin size={22} />
                                        </div>
                                        <div className="space-y-1">
                                            <div className="text-sm font-semibold text-slate-800">
                                                {item.ResidentialAddress || item.OfficeAddress || 'Address not available'}
                                            </div>
                                            <div className="text-xs font-medium text-slate-500">
                                                {item.City}, {item.State}, {item.Country} - {item.PinCode}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    />
                ) : (
                    <PremiumModuleWrapper
                        moduleName="Conversion"
                        loading={isLoading}
                        isInitialLoad={isInitialLoad}
                        data={conversions}
                        columns={columns}
                        totalRecords={totalRecord}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setItemsPerPage}
                        rowSelection={rowSelection}
                        onManualRowSelectionChange={setRowSelection}
                        showCards={false}
                        getRowCanExpand={() => mode === conversionTypes.CONSOLIDATED}
                        renderSubComponent={({ row }) => <ConversionDetailSubComponent leadNo={row.original.LeadNo} />}
                    />
                )}
            </div>
        </div>
    );
};

export default Conversions;
